import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { onAuthChange, getCurrentUser } from '../services/firebase/auth';
import { getClimber, createClimber } from '../services/firebase/climber';
import { Climber } from '../models/user/Climber';

interface AuthContextProps {
  currentUser: User | null;
  climberProfile: Climber | null;
  loading: boolean;
  refreshClimberProfile: () => Promise<void>;
  lastVisitedGym: string | null;
  setLastVisitedGym: (gymId: string) => void;
}

const AuthContext = createContext<AuthContextProps>({
  currentUser: null,
  climberProfile: null,
  loading: true,
  refreshClimberProfile: async () => {},
  lastVisitedGym: null,
  setLastVisitedGym: () => {}
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [climberProfile, setClimberProfile] = useState<Climber | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastVisitedGym, setLastVisitedGymState] = useState<string | null>(() => {
    // Load from localStorage on initialization
    return localStorage.getItem('lastVisitedGym');
  });

  // Function to set last visited gym and persist to localStorage
  const setLastVisitedGym = (gymId: string) => {
    setLastVisitedGymState(gymId);
    localStorage.setItem('lastVisitedGym', gymId);
  };

  // Function to fetch climber profile data
  const fetchClimberProfile = async (userId: string) => {
    try {
      let climber = await getClimber(userId);
      
      // If climber profile doesn't exist, create one
      if (!climber && currentUser) {
        climber = await createClimber(
          userId, 
          currentUser.displayName || undefined, 
          currentUser.displayName || undefined
        );
      }
      
      setClimberProfile(climber);
    } catch (error) {
      console.error('Error fetching climber profile:', error);
      setClimberProfile(null);
    }
  };

  // Function to refresh climber profile
  const refreshClimberProfile = async () => {
    if (currentUser) {
      await fetchClimberProfile(currentUser.uid);
    }
  };

  useEffect(() => {
    // Set initial user if logged in
    const user = getCurrentUser();
    if (user) {
      setCurrentUser(user);
      fetchClimberProfile(user.uid);
    }

    // Listen for auth state changes
    const unsubscribe = onAuthChange(async (user) => {
      setCurrentUser(user);
      
      if (user) {
        await fetchClimberProfile(user.uid);
      } else {
        setClimberProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    climberProfile,
    loading,
    refreshClimberProfile,
    lastVisitedGym,
    setLastVisitedGym
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
